import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    Box, Typography, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, Grid, Card, Stack, Chip, CircularProgress,
    Autocomplete, TextField
} from "@mui/material";
import { collection, getDocs, doc, setDoc, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";

function Studentattendance() {
    const legendItems = [
        { label: "Present", color: "#e8f5e9", border: "#a5d6a7" },
        { label: "Absent", color: "#ffebee", border: "#ef9a9a" },
        { label: "Holiday", color: "#f5f5f5", border: "#e0e0e0" },
    ];

    const [studentList, setStudentList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [attendanceData, setAttendanceData] = useState({});
    const [viewDate, setViewDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);
    const [status, setStatus] = useState("Present");
    const [counts, setCounts] = useState({ present: 0, absent: 0 });

    useEffect(() => { 
        loadStudents(); 
        loadAttendance(); 
    }, []);

    useEffect(() => {
        if (selectedStudent) calculateMonthlyStats(viewDate);
    }, [attendanceData, selectedStudent, viewDate]);

    const loadStudents = async () => {
        try {
            const admissionQuery = query(collection(db, "admission"), where('status', '==', 'Active'));
            
            const [admSnap, stuSnap, couSnap] = await Promise.all([
                getDocs(admissionQuery),
                getDocs(collection(db, "students")),
                getDocs(collection(db, "courses"))
            ]);

            const studentsMap = {};
            stuSnap.docs.forEach(d => { studentsMap[d.id] = d.data(); });

            const coursesMap = {};
            couSnap.docs.forEach(d => { coursesMap[d.id] = d.data(); });

            const combined = admSnap.docs.map(doc => {
                const adm = doc.data();
                const sData = studentsMap[adm.studentId] || {};
                const cData = coursesMap[adm.class] || {};

                return {
                    id: doc.id,
                    studentId: adm.studentId,
                    fullName: `${sData.firstname || ""} ${sData.fathername || ""} ${sData.surname || ""}`.trim() || "Unknown Student",
                    className: cData.name || "N/A",
                    rollno: adm.rollno || "N/A"
                };
            });

            // SORT ASCENDING BY FULL NAME
            const sortedList = combined.sort((a, b) => a.fullName.localeCompare(b.fullName));
            
            setStudentList(sortedList);
        } catch (error) {
            console.error("Error fetching student data:", error);
        }
    };

    const loadAttendance = async () => {
        const snapshot = await getDocs(collection(db, "studentAttendance"));
        let map = {};
        snapshot.docs.forEach(d => {
            const data = d.data();
            const key = `${data.admissionId}_${data.date}`;
            map[key] = { docId: d.id, ...data };
        });
        setAttendanceData(map);
    };

    const handleDateClick = (date) => {
        if (!selectedStudent) return;
        const dStr = format(date, "yyyy-MM-dd");
        const existing = attendanceData[`${selectedStudent.id}_${dStr}`];

        setSelectedDate(date);
        setStatus(existing?.status || "Present");
        setOpen(true);
    };

    const handleSave = async (customStatus = null) => {
        if (!selectedDate || !selectedStudent) return;
        setLoading(true);

        try {
            const dStr = format(selectedDate, "yyyy-MM-dd");
            const docId = `${selectedStudent.id}_${dStr}`;
            const finalStatus = customStatus || status;

            await setDoc(doc(db, "studentAttendance", docId), {
                admissionId: selectedStudent.id,
                studentId: selectedStudent.studentId,
                studentName: selectedStudent.fullName,
                className: selectedStudent.className,
                date: dStr,
                status: finalStatus,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            await loadAttendance();
            setOpen(false);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const calculateMonthlyStats = (activeDate) => {
        const monthStr = format(activeDate, "yyyy-MM");
        let p = 0, a = 0;
        Object.values(attendanceData).forEach(r => {
            if (r.admissionId === selectedStudent.id && r.date.startsWith(monthStr)) {
                if (r.status === "Present") p++;
                else if (r.status === "Absent") a++;
            }
        });
        setCounts({ present: p, absent: a });
    };

    return (
        <Box p={1}>
            <Grid className="row">
                <Grid className="col-sm-4 mb-3" item xs={12} md={4}>
                    <Stack spacing={3}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={2}>Student Search</Typography>
                            
                            {/* AUTOCOMPLETE FOR SEARCH & SELECT */}
                            <Autocomplete
                                options={studentList}
                                getOptionLabel={(option) => option.fullName}
                                value={selectedStudent}
                                onChange={(event, newValue) => setSelectedStudent(newValue)}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                        <Stack>
                                            <Typography variant="body2" fontWeight="bold">{option.fullName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.className} | Roll: {option.rollno}</Typography>
                                        </Stack>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Search Student Name..." 
                                        variant="outlined" 
                                        size="small" 
                                    />
                                )}
                            />
                        </Paper>

                        {selectedStudent && (
                            <Card sx={{ p: 2, bgcolor: '#1a237e', color: 'white', borderRadius: 3, textAlign: 'center' }}>
                                <Typography variant="caption">MONTHLY SUMMARY</Typography>
                                <Stack direction="row" justifyContent="space-around" mt={2}>
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold">{counts.present}</Typography>
                                        <Typography variant="caption">PRESENT</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" fontWeight="bold">{counts.absent}</Typography>
                                        <Typography variant="caption">ABSENT</Typography>
                                    </Box>
                                </Stack>
                            </Card>
                        )}

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle2" mb={2} fontWeight="bold">Status Legend</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {legendItems.map((item) => (
                                    <Chip 
                                        key={item.label}
                                        label={item.label}
                                        size="small"
                                        sx={{ bgcolor: item.color, border: `1px solid ${item.border}`, fontWeight: 'bold' }}
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>

                <Grid className="col-sm-8 mb-3" item xs={12} md={8}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 450 }}>
                        {selectedStudent ? (
                            <>
                                <Typography variant="h6" mb={2} color="primary" fontWeight="bold">
                                    {selectedStudent.fullName} <Typography component="span" color="text.secondary">({selectedStudent.className})</Typography>
                                </Typography>
                                <Calendar
                                    onClickDay={handleDateClick}
                                    tileClassName={({ date, view }) => {
                                        if (view !== 'month') return;
                                        const res = attendanceData[`${selectedStudent.id}_${format(date, "yyyy-MM-dd")}`]?.status;
                                        if (res === "Present") return "present-tile";
                                        if (res === "Absent") return "absent-tile";
                                        if (res === "Holiday") return "holiday-tile";
                                    }}
                                    onActiveStartDateChange={({ activeStartDate }) => setViewDate(activeStartDate)}
                                />
                            </>
                        ) : (
                            <Box sx={{ p: 10, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography>Select a student using search to view attendance</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Mark Attendance: {format(selectedDate || new Date(), "dd MMM yyyy")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant={status === "Present" ? "contained" : "outlined"}
                            color="success"
                            onClick={() => setStatus("Present")}
                            fullWidth
                        >
                            Present
                        </Button>
                        <Button
                            variant={status === "Absent" ? "contained" : "outlined"}
                            color="error"
                            onClick={() => setStatus("Absent")}
                            fullWidth
                        >
                            Absent
                        </Button>
                    </Stack>
                    <Button
                        variant={status === "Holiday" ? "contained" : "text"}
                        color="inherit"
                        onClick={() => setStatus("Holiday")}
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Mark as Holiday
                    </Button>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleSave()} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : "Save Attendance"}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
                .react-calendar { width: 100% !important; border: none !important; font-family: inherit; }
                .react-calendar__tile { 
                    height: 80px !important; 
                    border-radius: 12px !important; 
                    transition: 0.2s; 
                    border: 2px solid transparent !important; 
                    margin: 2px 0;
                }
                .present-tile { background: #e8f5e9 !important; color: #2e7d32 !important; border-color: #a5d6a7 !important; font-weight: bold; }
                .absent-tile { background: #ffebee !important; color: #d32f2f !important; border-color: #ef9a9a !important; font-weight: bold; }
                .holiday-tile { background: #f5f5f5 !important; color: #9e9e9e !important; border-color: #e0e0e0 !important; }
                .react-calendar__tile:hover { background: #f0f0f0 !important; transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                .react-calendar__tile--now { background: #fff9c4 !important; border-color: #fbc02d !important; }
                .react-calendar__navigation button:enabled:hover { background-color: #f5f5f5; border-radius: 8px; }
            `}</style>
        </Box>
    );
}

export default Studentattendance;